import { ReactElement, useState } from 'react';
import { toast } from 'react-toastify';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Button from '../../controls/Button';
import authenticateService from '../../services/authenticateService';
import { RHFValidations, TOAST_MESSAGES } from '../../constants/global-constants';
import CustomInputBox from '../../controls/CustomInputBox';
import { AwsLoginErrors } from '../../constants/telosair-interfaces';

type FORMDATA = {
  otp: string;
};

/**
 * Verify OTP Component for Account Activation.
 * @returns - Recat component for Verify OTP UI.
 */
export default function VerifyOtpComponent(): ReactElement {
  const [param] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const encodedEmail = param.get('email');
  const email = decodeURIComponent(encodedEmail || '');

  const methods = useForm<FORMDATA>({
    mode: 'all'
  });
  const { handleSubmit } = methods;

  /**
   * Function to verify OTP.
   * @param data - Form data.
   */
  const onVerify = async (data: FORMDATA): Promise<void> => {
    setLoading(true);
    if (email) {
      const { otp } = data;
      const { error } = await authenticateService.verifyConfirmationCode(email, otp);
      if (!error) {
        setLoading(false);
        toast.success('Account verirified successfully');
        navigate('/auth/login');
        return;
      }
      if (error === AwsLoginErrors.codeMismatch) {
        toast.error('Invalid verification code provided, please try again');
        setLoading(false);
        return;
      }
      if (error === AwsLoginErrors.notFound) {
        toast.error('User not found');
        setLoading(false);
        return;
      }
      setLoading(false);
      toast.error(TOAST_MESSAGES.error);
    }
  };
  /**
   * Function to resend OTP.
   */
  const onResend = (): void => {
    if (email) {
      authenticateService.resendConfirmationCode(
        email,
        (resendFailed: boolean) => !resendFailed && toast.success('OTP re-sent successfully')
      );
    }
  };
  return (
    <div className="flex flex-col gap-8 w-full">
      <div className="dark:bg-container-700 bg-white px-6 py-14 rounded w-full flex gap-4">
        <div className="h-12 w-12 bg-success-500 flex justify-center items-center rounded-full">
          <span className="material-icons-outlined text-white">done</span>
        </div>
        <div>
          {/* eslint-disable-next-line */}
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onVerify)}>
              <div className="text-xl font-semibold text-white">Verify your account</div>
              <div className="mt-1 text-gray-400">
                An verification code has been sent to{' '}
                <span className="text-gray-100">
                  {email}{' '}
                  <span
                    role="button"
                    tabIndex={0}
                    onKeyDown={onResend}
                    onClick={onResend}
                    className="cursor-pointer text-primary-500 underline"
                  >
                    resend
                  </span>
                </span>
              </div>
              <div>
                <CustomInputBox
                  label="Verification Code"
                  idname="otp"
                  type="number"
                  validationObject={RHFValidations.otp}
                />
                <div className="mt-12 w-full">
                  <Button
                    isBlockButton
                    buttonType="submit"
                    loading={loading}
                    theme="primary"
                    size="sm"
                    name="Verify"
                  />
                </div>
              </div>
            </form>
          </FormProvider>
        </div>
      </div>
      <div className="flex justify-center items-center flex-row gap-3">
        <div className="font-medium text-gray-500">Trouble Activating?</div>
        <div className="w-40">
          <Button isBlockButton size="sm" theme="secondary-color" name="Contact Support" />
        </div>
      </div>
    </div>
  );
}
